import type { Gender } from "./Contact";

export type RegisterAuthenticationStrategies =
  "email-credential" | "facebook-credential" | "google-credential" | "kakao-credential";

export type LoginAuthenticationStrategies =
  RegisterAuthenticationStrategies | "phone-number-credential" | "one-time-limited-token";

export type LoginVariables = {
  email?: string;
  password?: string;
  strategy?: LoginAuthenticationStrategies;
  strategy_data?: Record<string, string | number>;
  keepLoggedIn?: boolean;
  extraData?: Record<string, boolean>;
};

export type SocialRegistrationAttributes = {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

export type ConsentType = {
  id: string;
  accepted: boolean;
  one_off?: boolean;
};

export type OptionalConsentsLayout = "default" | "section" | "extended" | "single_option";

export type ConsentsGroupingType = "default" | "section" | "single_option";

export type RegisterSource = {
  source_type: string;
  source_id: string;
  representative_id: string;
  timestamp?: string;
  hash?: string;
  channel: "offline";
};

export type AcquisitionSource = {
  acquisition_source_app_id: string;
  acquisition_source_type: "online" | "offline";
  acquisition_source_app: string;
  place_name: string;
  place_channel_type: string;
  place_country: string;
};

export type RegistrationTrackingParameters = {
  channel_id?: string;
  channel_type?: string;
  country?: string;
  campaign_id?: string;
  content_id?: string;
  guided_trial?: boolean;
  program_id?: string;
};

type CustomAttributeBehaviour = "editable" | "readonly" | "hidden";

export type CustomAttributeDefinition = {
  code: string;
  display_after: string | null;
  label: string | null;
  hint: string | null;
  validation: string | null;
  type: string;
  is_required: boolean;
  behaviour: CustomAttributeBehaviour[];
  is_address_field?: boolean;
  clearable?: boolean;
  is_profile_location_field?: boolean;
  valid_values?: string[];
};

type ConsentsDefinition = {
  id: string;
  accepted?: boolean;
  one_off?: boolean;
};
export type CustomAttributeValues = {
  language?: string;
  preferredLanguage?: string;
  consents?: Array<ConsentsDefinition>;
  province?: string;
  [key: string]: string | Array<ConsentsDefinition> | undefined;
};

export type RegisterVariables = {
  firstname?: string;
  lastname?: string;
  email?: string;
  password?: string;
  phone?: string;
  date_of_birth: string;
  age_verification_uuid?: string;
  phone_validation_uuid?: string;
  email_validation_uuid?: string;
  device_code?: string;
  custom_attributes?: CustomAttributeValues;
  referral_code?: string;
  consents?: ConsentType[];
  gender?: Gender;
  source?: RegisterSource;
  acquisition_source?: AcquisitionSource;
  tracking?: RegistrationTrackingParameters;
  strategy?: RegisterAuthenticationStrategies;
  strategy_data?: Record<string, string | number>;
  profile_location?: Record<string, unknown>;
};

export type RegisterCdpVariables = {
  email: string;
  password: string;
  consent: number;
  action_verification_uuid: string;
  verification_channel: string;
};

export type RegisterVariablesSuccess = {
  uuid?: string;
  authentication?: LoginResponse;
};

export type RegisterVariablesError = {
  message: string;
  errors: string[];
};

export type LoginResponse = {
  consent_renewal_required?: boolean;
  uuid: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  ciam_token: string;
  magento_token?: string;
  is_device_registered?: boolean;
  is_age_verified?: boolean;
  province_name?: string;
};

export type RegisterResponse = {
  userId: number;
};

export type VerifyProviderError = {
  error_code: string;
  message: string;
};

export type VerifyResponse = {
  verification_performed: boolean;
  is_verified: boolean;
  uuid: string;
  status_from_provider?: string;
  consumer_data?: {
    date_of_birth: string;
    phone: string | null;
    name: string | null;
  };
  error?: VerifyProviderError;
};

export type VerifyVariables = {
  user_email?: string;
  country_code: string;
  method_code: string;
  // TODO: Implement types
  fields?: { [key: string]: string }[] | any;
};

export type ResetPasswordResponse = {
  message: string;
};

export type VerifyUpdateEmailResponse = {
  personal_info: {
    email: string;
    firstname: string;
    lastname: string;
    date_of_birth: string;
    phone: string;
    gender?: Gender;
  };
  addresses: {
    id: number | string;
    country_id: string;
    city: string;
    street: string | string[];
    postcode: string | null;
    default_shipping: boolean;
    default_billing: boolean;
  }[];
};

export type VerifyUpdateEmailError = {
  statusCode: number;
};

export type PasswordRequirementsResponse = {
  minLength: number;
  minLowerCase: number;
  minUpperCase: number;
  minNumber: number;
  minSymbol: number;
  excludeUsername: boolean;
};

export type DsarRequestPayload = {
  firstName: string;
  lastName: string;
  email: string;
  requestTypes: string[];
  language: string;
  additionalData: Record<string, string | string[]>;
  market?: string;
  country?: string;
};
